import pdfMake from 'pdfmake/build/pdfmake';
import Logo from 'Icons/RMlogo.png';
import { SingleDropReportOptionsDTO } from './SingleDropReportOptions';
import ExperimentDtoClient from 'business/models/ExperimentDtoClient';
import { getParentPath, isNullOrUndefined } from 'utils/Global';
import {
  InspectionSetDTO,
  ProteinFormulationListDTO,
  WellImageDTO,
  SaveImageRequestDTO,
  WellDetailsState,
  ReportImageRequestDTO,
  ScoresOfWellDropsDTO,
} from 'business/models/Models';
import { TreeService } from 'business/services/TreeService';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { getRegionId } from 'components/commandpanel/Navigator/DRCIPWUtil';
import PlateService from 'business/services/PlateService';
import WellService from 'business/services/WellService';
import {
  Drop,
  Plate,
  WellIng,
  WellNavigationDetails,
} from 'components/Experiment/constants';
import { EReportTypes } from 'enums/EIconPanels';
import {
  createPdfWithPopupBlockerHandler,
  getScreenNameColumnsTable,
  getScreenNamesFromLayers,
  IMAGER_TEMP_NAME,
  roundToMaxDecimals,
  SINGLE_DROP_INFO_FIELD_SEPARATOR,
  SINGLE_DROP_SCREEN_SHORT_MAX_LENGTH,
  SINGLE_DROP_SCREEN_MEDIUM_MAX_LENGTH,
} from './utils';
import { WellImageWithInfoDTO } from './ReportsCommonModels';
import { WellDetailsStates } from 'business/models/CustomModels';
import ExperimentService from 'business/services/ExperimentService';
import { BATCH_SIZE, fonts } from './ReportsConstants';
pdfMake.fonts = { Roboto: fonts.Roboto };
const singleDropBlankImage = require('components/main/images/singleDropBlank.png');

export const TABLE_LAYOUT = {
  hLineWidth: function (i, node) {
    return 0.5;
  },
  vLineWidth: function (i, node) {
    return 0.5;
  },
};

const experimentService = new ExperimentService();
const treeService = new TreeService();

export async function generateSingleDropReportPDF(
  options: SingleDropReportOptionsDTO,
  expData: ExperimentDtoClient,
  wellDetails: WellNavigationDetails[],
  saveReport: boolean,
  onPopupBlocked?: () => void,
) {
  const pdfDefinition = {
    info: {
      title: expData.expName + ' - Single Drop Report',
      author: 'Rock Maker',
      subject: 'Experiment',
    },
    content: await CreateContent(options, expData, wellDetails),
    styles: {
      headerText: { fontSize: 10, bold: true, lineHeight: 1.5 },
      subheader: { fontSize: 9, bold: true },
      largeText: { fontSize: 10, lineHeight: 1.5 },
      labelText: { fontSize: 8, lineHeight: 1.5 },
      tableSubHeader: { bold: true, fillColor: '#cccccc' },
      tableAltRow: { fillColor: '#f5f5f5' },
      textBold: { bold: true },
    },
    defaultStyle: {
      fontSize: 7,
      lineHeight: 1.2,
    },
    pageMargins: [25, 50, 25, 30],
    header: getHeader(),

    pageBreakBefore: false,
  };

  createPdfWithPopupBlockerHandler(
    pdfMake,
    pdfDefinition,
    expData.expName + ' - Single Drop Report',
    saveReport,
    onPopupBlocked,
  );
}

async function CreateContent(
  options: SingleDropReportOptionsDTO,
  expData: ExperimentDtoClient,
  wellDetails: WellNavigationDetails[],
) {
  const content = [];
  const wellList: WellIng[] = [];
  expData.plateDto.forEach((m) => {
    m.ingredients.wellList.forEach((wl) => {
      wellDetails.forEach((wd) => {
        if (wd.wellNum === wl.wellNumber && wd.plateID === wl.plateId) {
          wellList.push(wl);
        }
      });
    });
  });

  const uniquePlateIds: Set<number> = new Set<number>();
  for (const well of wellDetails) {
    uniquePlateIds.add(well.plateID);
  }
  const plateInfos: Map<number, Plate> = new Map<number, Plate>();
  for (const plateId of uniquePlateIds) {
    plateInfos.set(
      plateId,
      expData.plateDto.find((plate) => plate.plateId === plateId),
    );
  }

  const allImages = await getWellImages(options, expData, wellDetails, wellList);
  const screenNames = getScreenNamesFromLayers(expData.layers);

  for (const wl of wellList) {
    const plateInfo: Plate = plateInfos.get(wl.plateId);
    const selectedDrop = wl.dropList.find((dl) => dl.dropNumber == options.dropNumber);
    let proteinInfo: any;
    if (!isNullOrUndefined(selectedDrop)) {
      const dropProtFomulationId = selectedDrop.proteinFormulationID;
      proteinInfo = plateInfo.ingredients.proteinFormulation[dropProtFomulationId];
    }

    const expParents = await treeService.getDocumentParentNodes(
      expData.experimentId,
      ETreeNodeTypes.Experiment,
      true,
    );
    const expPath = expParents ? getParentPath(expParents) : '';
    content.push('\n');
    content.push({
      text: [
        { text: 'Experiment: ', bold: true },
        { text: expPath },
        { text: SINGLE_DROP_INFO_FIELD_SEPARATOR },
        { text: 'Plate ID: ', bold: true },
        { text: String(plateInfo.plateId) },
        { text: SINGLE_DROP_INFO_FIELD_SEPARATOR },
        { text: 'Barcode: ', bold: true },
        { text: plateInfo.plateBarName.split(',')[2] },
        { text: SINGLE_DROP_INFO_FIELD_SEPARATOR },
        { text: `${wl.rowLetter}${wl.columnNumber}-${options.dropNumber}` },
      ],
      style: 'labelText',
    });
    content.push(
      getScreenNameColumnsTable(
        screenNames,
        'labelText',
        SINGLE_DROP_SCREEN_SHORT_MAX_LENGTH,
        SINGLE_DROP_SCREEN_MEDIUM_MAX_LENGTH,
      ),
    );
    content.push('\n');
    content.push({ text: 'General Information', style: 'headerText' });
    content.push(
      await getGeneralInfoTable(expData, wl, options, selectedDrop, plateInfo),
    );
    content.push('\n');

    if (options.proteinFormulation) {
      if (!isNullOrUndefined(proteinInfo)) {
        content.push({
          text: 'Sample: ' + proteinInfo.proteinInfo.name,
          style: 'headerText',
        });
        content.push(
          await getProteinInfoTable(
            expData,
            wellDetails,
            wl,
            options,
            selectedDrop,
            plateInfo,
          ),
        );
        content.push('\n');
        if (options.notes !== null) {
          content.push({ text: 'Notes: ', bold: true });
          content.push({ text: proteinInfo.proteinInfo.notes });
          content.push('\n');
        }
      }
    }
    if (options.proteinIngredients) {
      if (!isNullOrUndefined(proteinInfo)) {
        content.push(await getProteinIngredientsTable(expData, proteinInfo));
        content.push('\n');
      }
    }
    if (options.standardIngredients) {
      if (!isNullOrUndefined(proteinInfo)) {
        content.push(await getStandardIngredientsTable(proteinInfo, options));
        content.push('\n');
      }
    }
    if (options.wellCondition) {
      content.push(await getWellIngredientsTable(expData, wl, options));
      content.push('\n');
    }
    if (options.dropImage) {
      content.push({
        margin: [0, 0, 0, 10],
        table: {
          widths: ['100%'],
          body: [
            [
              {
                text: 'Drop Images(s)',
                margin: [0, 5, 0, 0],
                style: 'headerText',
                fillColor: '#808080',
                color: 'white',
              },
            ],
          ],
        },
      });
      if (!isNullOrUndefined(proteinInfo) && allImages?.length > 0) {
        content.push(
          await getDropImagesTable(
            options,
            expData,
            proteinInfo,
            wl,
            singleDropBlankImage,
            allImages,
          ),
        );
      }
      content.push('\n');
    }
    if (options.dropImageHistory) {
      content.push({
        margin: [0, 0, 0, 10],
        table: {
          widths: ['100%'],
          body: [
            [
              {
                text: 'Image History',
                margin: [0, 5, 0, 0],
                style: 'headerText',
                headlineLevel: 1,
                fillColor: '#808080',
                color: 'white',
              },
            ],
          ],
        },
      });
      if (!isNullOrUndefined(proteinInfo) && allImages?.length > 0) {
        content.push(
          await getDropImagesHistoryTable(
            options,
            expData,
            proteinInfo,
            wl,
            singleDropBlankImage,
            allImages,
          ),
        );
      }
      content.push('\n');
    }
  }

  return content;
}

async function getGeneralInfoTable(
  expData: ExperimentDtoClient,
  wellInfo: WellIng,
  options: SingleDropReportOptionsDTO,
  selectedDrop: Drop,
  plateInfo: Plate,
) {
  let dropWellVolume = 0;
  let dropProteinVolume = 0;
  if (!isNullOrUndefined(selectedDrop)) {
    dropWellVolume = selectedDrop.dropWellVolume;
    dropProteinVolume = selectedDrop.dropProteinVolume;
  }
  const captureResultProperties = await experimentService.getExperimentImageSettings({
    cpID: Math.min(...(options.captureProfiles?.map((p) => p.id) || [])), // oldest capture profile id first (usually Visible)
    dropNumber: options.dropNumber,
    plateID: wellInfo.plateId,
    wellNum: wellInfo.wellNumber,
    regionName: 'Drop', // drop region only
    inspectionSet: expData.inspections?.[expData.inspections.length - 1], // check in latest inspection
  } as WellDetailsStates);
  const imagerTemp =
    captureResultProperties
      ?.flatMap((c) => c.results)
      .find((r) => r.name === IMAGER_TEMP_NAME)?.value || null;

  return {
    table: {
      widths: ['34%', '33%', '33%'],
      body: [
        [
          { text: 'Imager Temperature: ' + (imagerTemp ? imagerTemp + ' °C' : '') },
          {
            text:
              'Drop/Protein(uL): ' +
              roundToMaxDecimals(dropWellVolume) +
              '/' +
              roundToMaxDecimals(dropProteinVolume),
          },
          { text: 'Well: ' + roundToMaxDecimals(wellInfo.wellVolume) + ' µL' },
        ],
        [
          { text: 'Plate ID: ' + plateInfo.plateId },
          { text: 'Plate Barcode: ' + plateInfo.plateBarName.split(',')[2] },
          {
            text:
              'Well: ' +
              wellInfo.rowLetter +
              wellInfo.columnNumber +
              ', ' +
              'Drop: ' +
              options.dropNumber,
          },
        ],
      ],
    },
    layout: TABLE_LAYOUT,
  };
}

async function getProteinInfoTable(
  expData: ExperimentDtoClient,
  wellDetails: any[],
  wellInfo: WellIng,
  options: SingleDropReportOptionsDTO,
  selectedDrop: Drop,
  plateInfo: Plate,
) {
  //let dropProtFomulationId = wellInfo.dropList[options.dropNumber-1].proteinFormulationID
  const dropProtFomulationId = selectedDrop.proteinFormulationID;
  const proteinInfo = plateInfo.ingredients.proteinFormulation[dropProtFomulationId];
  return {
    table: {
      widths: ['34%', '33%', '33%'],
      body: [
        [
          {
            text: 'Sample Details',
            bold: true,
            colSpan: 3,
            fillColor: '#808080',
            color: 'white',
            style: 'labelText',
          },
          {},
          {},
        ],
        [
          { text: 'ID: ' + dropProtFomulationId },
          { text: 'Protein Lot ID: ' + proteinInfo.proteinInfo.proteinLotId },
          { text: 'Liquid Class: ' + proteinInfo.proteinInfo.liquidClassName },
        ],
        [
          { text: 'Class: ' + proteinInfo.proteinInfo.pclass },
          {
            text:
              'Date Prepared: ' +
              new Date(proteinInfo.proteinInfo.datePrepared).toLocaleTimeString() +
              ' ' +
              new Date(proteinInfo.proteinInfo.datePrepared).toLocaleDateString('en-US'),
          },
          { text: 'Ext. Coefficient: ' + proteinInfo.proteinInfo.extCoefficient },
        ],
        [
          { text: 'Volume (uL): ' + roundToMaxDecimals(proteinInfo.proteinInfo.volume) },
          { text: 'Storage Temperature (C): ' + proteinInfo.proteinInfo.storageTemp },
          { text: 'Notebook Page No: ' + (proteinInfo.proteinInfo.noteBookPageNo ?? '') },
        ],
      ],
    },
    layout: TABLE_LAYOUT,
  };
}

async function getProteinIngredientsTable(
  expData: ExperimentDtoClient,
  proteinInfo: ProteinFormulationListDTO,
) {
  const body: any = [
    [
      {
        text: 'Sample Ingredients',
        bold: true,
        colSpan: 7,
        fillColor: '#808080',
        style: 'labelText',
        color: 'white',
      },
      {},
      {},
      {},
      {},
      {},
      {},
    ],
    [
      { text: 'Name', bold: true, fillColor: '#cccccc' },
      { text: 'Type', bold: true, fillColor: '#cccccc' },
      { text: 'ID', bold: true, fillColor: '#cccccc' },
      { text: 'Lot', bold: true, fillColor: '#cccccc' },
      { text: 'MW (kDa)', bold: true, fillColor: '#cccccc' },
      { text: 'Conc. (mg/mL)', bold: true, fillColor: '#cccccc' },
      { text: 'Conc. (mM)', bold: true, fillColor: '#cccccc' },
    ],
  ];
  proteinInfo.ingredients.forEach((p) => {
    body.push([
      p.name,
      p.typeName,
      p.id,
      p.lot,
      p.moleculerWeight,
      p.concentrationmgml,
      p.concentrationmM,
    ]);
  });
  return {
    table: {
      widths: ['16%', '14%', '14%', '14%', '14%', '14%', '14%'],
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

async function getStandardIngredientsTable(
  proteinInfo: ProteinFormulationListDTO,
  options: SingleDropReportOptionsDTO,
) {
  const body: any = [
    [
      {
        text: 'Sample Standard Ingredients',
        bold: true,
        colSpan: 8,
        fillColor: '#808080',
        style: 'labelText',
        color: 'white',
      },
      {},
      {},
      {},
      {},
      {},
      {},
      {},
    ],
    [
      { text: 'Name', bold: true, fillColor: '#cccccc' },
      { text: 'Type', bold: true, fillColor: '#cccccc' },
      { text: 'Stock Conc.', bold: true, fillColor: '#cccccc' },
      { text: 'Conc. Units', bold: true, fillColor: '#cccccc' },
      { text: 'Stock Low pH', bold: true, fillColor: '#cccccc' },
      { text: 'Stock High pH', bold: true, fillColor: '#cccccc' },
      { text: 'Conc.', bold: true, fillColor: '#cccccc' },
      { text: 'pH', bold: true, fillColor: '#cccccc' },
    ],
  ];

  proteinInfo.standardIngredients.forEach((p) => {
    body.push([
      p.ingredientName,
      p.type,
      p.stockConcentration,
      p.stockConcentrationUnit,
      p.stockpH,
      p.highpHStockpH,
      p.concentrationM,
      p.pH,
    ]);
  });

  return {
    table: {
      widths: ['35%', '10%', '10%', '10%', '10%', '10%', '8%', '7%'],
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

async function getWellIngredientsTable(
  expData: ExperimentDtoClient,
  wellInfo: WellIng,
  options: SingleDropReportOptionsDTO,
) {
  const body: any = [
    [
      {
        text: 'Well Ingredients',
        bold: true,
        fillColor: '#808080',
        color: 'white',
        style: 'labelText',
      },
    ],
  ];

  let ingName = '';
  wellInfo.wellLayerList.forEach((wlaylist) => {
    wlaylist.wellLayerIngredientList.forEach((layIng) => {
      options.longNameIngredientsWell
        ? (ingName = layIng.ingredientStockIngredient.longName)
        : (ingName = layIng.ingredientStockIngredient.shortName);
      body.push([
        roundToMaxDecimals(layIng.preMixedConcentration) +
          ' ' +
          layIng.ingredientStock.concentrationUnits +
          ' ' +
          ingName +
          (layIng.pH ? ' pH ' + layIng.pH : ''),
      ]);
    });
  });

  return {
    table: {
      widths: ['50%'],
      body: body,
    },
    layout: TABLE_LAYOUT,
  };
}

async function getWellImages(
  options: SingleDropReportOptionsDTO,
  expData: ExperimentDtoClient,
  wellSelectionDetails: WellNavigationDetails[],
  wellInfos: WellIng[],
): Promise<WellImageWithInfoDTO[]> {
  const result: WellImageWithInfoDTO[] = [];
  if (options.captureProfiles.filter((cp) => cp.selected).length === 0) {
    return result;
  }
  const inspections: InspectionSetDTO[] = [];
  const selectedInspection: InspectionSetDTO = options.selectedInspection;
  if (isNullOrUndefined(selectedInspection?.dateImaged)) {
    return result;
  }
  const wellService: WellService = new WellService();
  const uniquePlateIds: Set<number> = new Set<number>();
  for (const well of wellSelectionDetails) {
    uniquePlateIds.add(well.plateID);
  }
  const dropNumber: number = options.dropNumber;
  const plates: Plate[] = [];
  for (const plateId of [...uniquePlateIds]) {
    plates.push(expData.plateDto.find((plate) => plate.plateId === plateId));
  }
  for (const inspectionSet of expData.inspections) {
    inspections.push(inspectionSet);
  }
  const selectedCaptureProfileIds: Set<number> = new Set<number>();
  for (const captureProfile of options.captureProfiles) {
    if (captureProfile.selected === true) {
      selectedCaptureProfileIds.add(captureProfile.id);
    }
  }

  const imageDto: SaveImageRequestDTO = new SaveImageRequestDTO();
  imageDto.saveAllRegions = true;
  imageDto.saveAllWellDropScores = true;
  imageDto.saveAnnotations = false;
  imageDto.selectedCaptureProfileIds = [...selectedCaptureProfileIds];
  imageDto.inspectionSetDTOs = [];
  imageDto.saveAllDrops = false;
  imageDto.saveAllFocusLevels = false;
  imageDto.saveAllInspections = false;
  imageDto.useBestFocusLevelImages = options.useBestFocusLevel;
  imageDto.imageZoomDto = options.imageZoom;
  imageDto.wellDetails = [];
  for (const well of wellSelectionDetails) {
    const wellInfo = expData.plateDto
      .find((plate) => plate.plateId === well.plateID)
      .ingredients.wellList.find(
        (ingredientWell) => ingredientWell.wellNumber === well.wellNum,
      );

    for (const inspection of inspections) {
      for (const groupedInspect of inspection.groupedInspections) {
        for (const captureProfileId of selectedCaptureProfileIds) {
          const wellDetailState = new WellDetailsState();
          wellDetailState.cpID = captureProfileId;
          wellDetailState.dropNumber = dropNumber;
          wellDetailState.imageBatchId = groupedInspect.imageBatchId;
          wellDetailState.plateID = well.plateID;
          wellDetailState.regionID = getRegionId(
            expData.regions,
            well.wellNum,
            dropNumber,
            'Drop',
            well.plateID,
          );
          wellDetailState.regionName = 'Drop';
          wellDetailState.wellName = wellInfo.rowLetter + wellInfo.wellNumber;
          wellDetailState.wellNum = wellInfo.wellNumber;
          wellDetailState.saveAnnotations = false;
          imageDto.wellDetails.push(wellDetailState);
          if (inspection.id === selectedInspection.id) {
            const zoomedWellDetailState = new WellDetailsState(wellDetailState);
            zoomedWellDetailState.useCurrentZoom = options.useCurrentZoom;
            zoomedWellDetailState.saveAnnotations = true;
            imageDto.wellDetails.push(zoomedWellDetailState);
          }
        }
      }
    }
  }

  const reportImageRequest = new ReportImageRequestDTO();
  reportImageRequest.reportType = EReportTypes.SingleDropReport;
  reportImageRequest.saveImageRequestDTO = imageDto;
  const wellDetails = imageDto.wellDetails ?? [];
  let dropImages: WellImageDTO[] = [];

  if (wellDetails.length <= BATCH_SIZE) {
    dropImages = await wellService.getWellImagesWithDrawingsAnnotations(
      reportImageRequest,
    );
  } else {
    const batchPromises: Promise<WellImageDTO[]>[] = [];

    for (let i = 0; i < wellDetails.length; i += BATCH_SIZE) {
      const batchImageDto = new SaveImageRequestDTO();
      Object.assign(batchImageDto, imageDto);
      batchImageDto.wellDetails = wellDetails.slice(i, i + BATCH_SIZE);

      const batchRequest = new ReportImageRequestDTO();
      batchRequest.reportType = EReportTypes.SingleDropReport;
      batchRequest.saveImageRequestDTO = batchImageDto;

      batchPromises.push(wellService.getWellImagesWithDrawingsAnnotations(batchRequest));
    }

    const batchResults = await Promise.all(batchPromises);
    dropImages = batchResults.flat();
  }

  const plateScores = new Map<number, ScoresOfWellDropsDTO[][]>();
  for (const plate of uniquePlateIds) {
    plateScores[plate] = await getPlateScores(plate);
  }

  for (const dropImage of dropImages) {
    const newImage = new WellImageWithInfoDTO(dropImage);

    if (!newImage.key) {
      continue;
    }

    const splitKey = newImage.key.split('-');
    const plateId = Number(splitKey[0]);
    const wellNumber = Number(splitKey[1]);
    const dropNumber = Number(splitKey[2]);
    const batchId = Number(splitKey[3]);

    newImage.inspectionSet = inspections.find((inspect) =>
      inspect.groupedInspections.some(
        (groupInspect) =>
          groupInspect.plateId === plateId && groupInspect.imageBatchId === batchId,
      ),
    );
    newImage.isSelectedInspection = newImage.inspectionSet.id === selectedInspection.id;

    let score: ScoresOfWellDropsDTO | undefined;
    plateScores[plateId].some(
      (wellScores) =>
        (score = wellScores.find(
          (wellScore) =>
            wellScore.wellNumber === wellNumber &&
            wellScore.dropNumber === dropNumber &&
            wellScore.scoreBatchId === batchId,
        )) !== undefined,
    );
    newImage.score = score;

    newImage.cp = options.captureProfiles.find(
      (captureProfile) =>
        captureProfile.id === newImage.captureResultTuningValuesDto.cpId,
    );

    newImage.dropNumber = dropNumber;

    newImage.wellInfo = wellInfos.find(
      (wellInfo) => wellInfo.wellNumber === wellNumber && wellInfo.plateId === plateId,
    );
    result.push(newImage);
  }

  return result;
}

async function getDropImagesTable(
  options: SingleDropReportOptionsDTO,
  expData: ExperimentDtoClient,
  proteinInfo: ProteinFormulationListDTO,
  wellInfo: WellIng,
  singleDropBlankImage: string,
  allImages: WellImageWithInfoDTO[],
) {
  const rows: any = [];
  let columns: any = [];
  let colCount = 1;

  const dropImages: WellImageWithInfoDTO[] = [];

  for (const captureProfile of options.captureProfiles.filter((cp) => cp.selected)) {
    const dropImage = allImages.find(
      (image) =>
        image.wellNumber === wellInfo.wellNumber &&
        image.isSelectedInspection &&
        image.cp.id === captureProfile.id &&
        (image.isZoomed === options.useCurrentZoom || options.imageZoom === null) &&
        image.isAnnotated === true,
    );
    if (!isNullOrUndefined(dropImage)) {
      dropImages.push(dropImage);
    } else {
      const emptyImage = new WellImageWithInfoDTO();
      emptyImage.cp = captureProfile;
      emptyImage.inspectionSet = expData.inspections.reduce(
        (latest, current) =>
          !latest || current.dateImaged > latest.dateImaged ? current : latest,
        undefined as InspectionSetDTO | undefined,
      );
      emptyImage.wellInfo = wellInfo;
      emptyImage.wellNumber = wellInfo.wellNumber;
      emptyImage.wellImage = null;
      dropImages.push(emptyImage);
    }
  }

  dropImages.forEach((dImg) => {
    if (dImg.wellImage !== null) {
      if (options.dateImaged === true) {
        const color = '#FFFFFF';
        let score;
        columns.push({
          width: 'auto',
          table: {
            headerRows: 3,
            body: [
              [
                {
                  table: {
                    widths: ['auto', 103],
                    body: [
                      [
                        {
                          border: [false, false, false, false],
                          fillColor: color,
                          text: score,
                        },
                        {
                          border: [false, false, false, false],
                          text:
                            dImg.wellInfo.rowLetter +
                            dImg.wellInfo.columnNumber +
                            '-' +
                            options.dropNumber +
                            '   ' +
                            dImg.cp.name,
                          bold: true,
                          alignment: 'center',
                        },
                      ],
                    ],
                  },
                },
              ],
              [
                {
                  image: dImg.wellImage,
                  width: 134,
                  margin: [-4, -2, -4, -3],
                },
              ],
              [
                {
                  text:
                    new Date(dImg.inspectionSet.dateImaged).toLocaleTimeString() +
                    ' ' +
                    new Date(dImg.inspectionSet.dateImaged).toLocaleDateString('en-US'),
                  alignment: 'center',
                },
              ],
            ],
          },
        });
      } else {
        const color = '#FFFFFF';
        let score;
        columns.push({
          width: 'auto',
          table: {
            headerRows: 2,
            body: [
              [
                {
                  table: {
                    widths: ['auto', 103],
                    body: [
                      [
                        {
                          border: [false, false, false, false],
                          fillColor: color,
                          text: score,
                        },
                        {
                          border: [false, false, false, false],
                          text:
                            dImg.wellInfo.rowLetter +
                            dImg.wellInfo.columnNumber +
                            '-' +
                            options.dropNumber +
                            '   ' +
                            dImg.cp.name,
                          bold: true,
                          alignment: 'center',
                        },
                      ],
                    ],
                  },
                },
              ],
              [
                {
                  image: dImg.wellImage,
                  width: 134,
                  height: 110,
                  margin: [-4, -2, -4, -3],
                },
              ],
            ],
          },
        });
      }
    } else {
      columns.push({
        width: 'auto',
        table: {
          headerRows: 3,
          body: [
            [
              {
                table: {
                  widths: ['auto', 103],
                  body: [
                    [
                      {
                        border: [false, false, false, false],
                        text: '',
                      },
                      {
                        border: [false, false, false, false],
                        text:
                          dImg.wellInfo.rowLetter +
                          dImg.wellInfo.columnNumber +
                          '-' +
                          options.dropNumber +
                          '   ' +
                          dImg.cp.name,
                        bold: true,
                        alignment: 'center',
                      },
                    ],
                  ],
                },
              },
            ],
            [
              {
                width: 134,
                height: 110,
                margin: [-4, -2, -4, -3],
                image: singleDropBlankImage,
              },
            ],
            [
              {
                text:
                  new Date(dImg.inspectionSet.dateImaged).toLocaleTimeString() +
                  new Date(dImg.inspectionSet.dateImaged).toLocaleDateString('en-US'),
                alignment: 'center',
              },
            ],
          ],
        },
      });
    }
    if (colCount == dropImages.length || (colCount > 0 && colCount % 4 == 0)) {
      rows.push({
        alignment: 'justify',
        width: '*',
        columns: columns,
      });
      rows.push('\n');
      columns = [];
    }
    colCount++;
  });
  return rows;
}

async function getDropImagesHistoryTable(
  options: SingleDropReportOptionsDTO,
  expData: ExperimentDtoClient,
  proteinInfo: ProteinFormulationListDTO,
  wellInfo: WellIng,
  singleDropBlankImage: string,
  allImages: WellImageWithInfoDTO[],
) {
  const plateInspections: InspectionSetDTO[] = [];
  const regionId = getRegionId(
    expData.regions,
    wellInfo.wellNumber,
    options.dropNumber,
    'Drop',
    wellInfo.plateId,
  );
  expData.inspections.forEach((ins) => {
    ins.groupedInspections.forEach((gpIns) => {
      if (gpIns.plateId === wellInfo.plateId) {
        plateInspections.push(ins);
      }
    });
  });

  const dropImages: WellImageWithInfoDTO[] = [];

  if (!isNullOrUndefined(regionId)) {
    if (options.sortImage === 'byInspection') {
      for (const insp of plateInspections) {
        for (const cp of options.captureProfiles) {
          if (cp.selected === true) {
            const currentImage = allImages.find((image) => {
              return (
                image.cp.id === cp.id &&
                image.inspectionSet.id === insp.id &&
                image.wellNumber === wellInfo.wellNumber &&
                image.wellInfo.plateId === wellInfo.plateId &&
                image.isZoomed === false &&
                image.isAnnotated === false
              );
            });
            if (!isNullOrUndefined(currentImage)) {
              dropImages.push(currentImage);
            } else {
              const emptyImage = new WellImageWithInfoDTO();
              emptyImage.cp = cp;
              emptyImage.inspectionSet = insp;
              emptyImage.wellInfo = wellInfo;
              emptyImage.wellNumber = wellInfo.wellNumber;
              emptyImage.wellImage = null;
              dropImages.push(emptyImage);
            }
          }
        }
      }
    } else {
      for (const cp of options.captureProfiles) {
        if (cp.selected === true) {
          for (const inspection of plateInspections) {
            const currentImage = allImages.find(
              (image) =>
                image.cp.id === cp.id &&
                image.inspectionSet.id === inspection.id &&
                image.wellNumber === wellInfo.wellNumber &&
                image.wellInfo.plateId === wellInfo.plateId,
            );
            if (!isNullOrUndefined(currentImage)) {
              dropImages.push(currentImage);
            } else {
              const emptyImage = new WellImageWithInfoDTO();
              emptyImage.cp = cp;
              emptyImage.inspectionSet = inspection;
              emptyImage.wellInfo = wellInfo;
              emptyImage.wellNumber = wellInfo.wellNumber;
              emptyImage.wellImage = null;
              dropImages.push(emptyImage);
            }
          }
        }
      }
    }
  }

  const rows: any = [];
  let columns: any = [];
  let colCount = 1;
  let captureProfileName = '';
  let inspectionName = '';
  let dropImageIndex = 1;

  if (isNullOrUndefined(dropImages) || dropImages.length == 0) {
    return rows;
  }

  dropImages.forEach((dImg) => {
    let captureProfileHeader = dImg.cp.name;

    if (options.sortImage === 'byInspection') {
      if (inspectionName !== dImg.inspectionSet.name) {
        inspectionName = dImg.inspectionSet.name;
        rows.push('\n');
        rows.push({
          headlineLevel: 1,
          text:
            new Date(dImg.inspectionSet.dateImaged).toLocaleTimeString() +
            ' ' +
            new Date(dImg.inspectionSet.dateImaged).toLocaleDateString('en-US'),
          style: 'subheader',
        });
      }
    } else {
      captureProfileHeader = '';
      if (captureProfileName !== dImg.cp.name) {
        rows.push('\n');
        captureProfileName = dImg.cp.name;
        rows.push({ headlineLevel: 1, text: captureProfileName, style: 'subheader' });
      }
    }

    if (dImg.wellImage !== null) {
      const color = '#FFFFFF';
      let score;
      columns.push({
        width: 'auto',
        table: {
          headerRows: 3,
          body: [
            [
              {
                table: {
                  widths: ['auto', 100],
                  body: [
                    [
                      {
                        border: [false, false, false, false],
                        fillColor: color,
                        text: score,
                      },
                      {
                        border: [false, false, false, false],
                        text:
                          dImg.wellInfo.rowLetter +
                          dImg.wellInfo.columnNumber +
                          '-' +
                          options.dropNumber +
                          ' ' +
                          captureProfileHeader,
                        bold: true,
                        alignment: 'center',
                      },
                    ],
                  ],
                },
              },
            ],
            [
              {
                image: dImg.wellImage,
                width: 134,
                height: 110,
                margin: [-4, -2, -4, -3],
              },
            ],
            [
              {
                text:
                  new Date(dImg.inspectionSet.dateImaged).toLocaleTimeString() +
                  ' ' +
                  new Date(dImg.inspectionSet.dateImaged).toLocaleDateString('en-US'),
                alignment: 'center',
              },
            ],
          ],
        },
      });
    } else {
      columns.push({
        width: 'auto',
        table: {
          headerRows: 3,
          body: [
            [
              {
                table: {
                  widths: ['auto', 100],
                  body: [
                    [
                      {
                        border: [false, false, false, false],
                        text: '',
                      },
                      {
                        border: [false, false, false, false],
                        text:
                          dImg.wellInfo.rowLetter +
                          dImg.wellInfo.columnNumber +
                          '-' +
                          options.dropNumber +
                          ' ' +
                          captureProfileHeader,
                        bold: true,
                        alignment: 'center',
                      },
                    ],
                  ],
                },
              },
            ],
            [
              {
                width: 134,
                height: 110,
                margin: [-4, -2, -4, -3],
                image: singleDropBlankImage,
              },
            ],
            [
              {
                text:
                  new Date(dImg.inspectionSet.dateImaged).toLocaleTimeString() +
                  ' ' +
                  new Date(dImg.inspectionSet.dateImaged).toLocaleDateString('en-US'),
                alignment: 'center',
              },
            ],
          ],
        },
      });
    }

    let nextDropImageIndex: number;
    let addNewLine = false;
    const dropImagesLength = dropImages.length;
    if (dropImageIndex < dropImagesLength) {
      nextDropImageIndex = dropImageIndex;
    } else {
      nextDropImageIndex = dropImagesLength - 1;
    }
    if (options.sortImage === 'byInspection') {
      if (inspectionName != dropImages[nextDropImageIndex].inspectionSet.name) {
        addNewLine = true;
      }
    } else {
      if (captureProfileName != dropImages[nextDropImageIndex].cp.name) {
        addNewLine = true;
      }
    }

    if (
      dropImageIndex == dropImagesLength ||
      (colCount > 0 && colCount % 4 == 0) ||
      addNewLine
    ) {
      rows.push({
        defaultStyle: {
          columnGap: 0.5,
        },
        columns: columns,
      });
      rows.push('\n');
      columns = [];
      colCount = 0;
    }

    colCount++;
    dropImageIndex++;
  });

  return rows;
}

async function getPlateScores(plateId: number): Promise<ScoresOfWellDropsDTO[][]> {
  const plateService = new PlateService();
  return await plateService.getPlateScoreBasedOnWellsByPlateId(plateId);
}

function getHeader() {
  return [
    {
      columns: [
        {
          image: Logo,
          width: 20,
          height: 20,
        },
        {
          text: 'ROCK MAKER®',
          margin: [5, 4, 0, 0],
          width: 440,
          fontSize: 12,
          color: '#707070',
        },
        {
          text: 'Printed On: ' + new Date().toLocaleDateString('en-US'),
          margin: [0, 5, 0, 0],
          color: '#707070',
        },
      ],
      margin: [20, 20, 20, 20],
    },
  ];
}
